import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDailyChecklistItem,
  deleteDailyChecklistItem,
  listDailyChecklist,
  nextDoneAt,
  nextPendingItemId,
  sortDailyItems,
  updateDailyChecklistItem,
  type DailyChecklistItem,
} from '../../lib/dailyChecklist';

/**
 * One day's list, with every change applied before it is sent.
 *
 * Optimistic for the reason `TaskChecklist` gives: a checkbox that waits a round
 * trip to paint does not read as slow, it reads as broken, so people click it
 * again. Here the stake is higher, because a tick does not only paint -- it
 * moves the row to the bottom, and that is the feedback.
 *
 * Rollbacks are keyed by item id rather than by restoring a snapshot of the
 * array, because a second tick is routinely in flight when the first one fails
 * and putting the old array back would untick it too. On top of that each item
 * carries a monotonic stamp, so a slow reply that lost a race is dropped rather
 * than applied over a newer one -- the same guard `useDayTasks.patch` uses.
 *
 * Used by the dashboard card and, with `canEdit` false, by each expanded row on
 * the history page. One implementation of the optimism between the two.
 */
export function useDailyChecklist(day: string) {
  const [items, setItems] = useState<DailyChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  /** Split for the reason every other card here splits them: a failed tick
   *  still has a list, and blanking it takes away the row just clicked. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Read by the callbacks so every one of them is useCallback-stable and does
  // not rebuild the rows under the pointer on each keystroke.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const dayRef = useRef(day);
  dayRef.current = day;

  const patchStamps = useRef(new Map<string, number>());
  /** Bumped on every load, so a reply for the day you just stepped away from
   *  cannot land in the day you are now looking at. */
  const generation = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const sent = (generation.current += 1);
    setLoading(true);

    listDailyChecklist(day, controller.signal)
      .then((data) => {
        if (generation.current !== sent) return;
        setItems(sortDailyItems(data.items));
        setLoadError(null);
        setActionError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || generation.current !== sent) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load this day.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted && generation.current === sent) setLoading(false);
      });

    return () => controller.abort();
  }, [day]);

  const report = useCallback((failure: unknown, fallback: string) => {
    setActionError(failure instanceof Error ? failure.message : fallback);
  }, []);

  /** A stamp per item, so a reply knows whether it is still the newest word on
   *  that row. Returns null when it has been overtaken. */
  const claim = useCallback((id: string) => {
    const sent = (patchStamps.current.get(id) ?? 0) + 1;
    patchStamps.current.set(id, sent);
    return () => patchStamps.current.get(id) === sent;
  }, []);

  const replace = useCallback((id: string, patch: Partial<DailyChecklistItem>) => {
    setItems((current) =>
      sortDailyItems(current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))),
    );
  }, []);

  const add = useCallback(
    (title: string) => {
      // Read fresh rather than from state: `day` is what the composer was drawn
      // for, and this is what the row is actually written to.
      const target = dayRef.current;
      const pendingId = nextPendingItemId();
      const now = new Date().toISOString();

      setActionError(null);
      setItems((current) =>
        sortDailyItems([
          ...current,
          {
            id: pendingId,
            day: target,
            title,
            done: false,
            doneAt: null,
            createdAt: now,
            updatedAt: now,
          },
        ]),
      );

      // Not awaited: the composer has already cleared and refocused, so the next
      // line is being typed while this one is still in the air.
      void addDailyChecklistItem(target, title)
        .then(({ item }) => {
          if (dayRef.current !== target) return;
          setItems((current) =>
            sortDailyItems(current.map((entry) => (entry.id === pendingId ? item : entry))),
          );
        })
        .catch((addError: unknown) => {
          if (dayRef.current !== target) return;
          report(addError, 'Could not add that item.');
          setItems((current) => current.filter((entry) => entry.id !== pendingId));
        });
    },
    [report],
  );

  const toggle = useCallback(
    async (item: DailyChecklistItem) => {
      const current = claim(item.id);
      const done = !item.done;
      // Stamped so it sorts below everything already ticked, whatever the two
      // clocks think -- see `nextDoneAt`. Unticking clears it, which drops the
      // row back among the outstanding ones at its original typing position,
      // because `createdAt` was never touched. That is the whole argument for
      // not having a `position` column.
      const doneAt = done ? nextDoneAt(itemsRef.current) : null;

      setActionError(null);
      replace(item.id, { done, doneAt });

      try {
        const { item: saved } = await updateDailyChecklistItem(item.id, { done });
        if (!current()) return;
        // Re-sorted on the reply too: the server's stamp is the authority, and
        // on a skewed clock it can land either side of the optimistic one.
        setItems((entries) =>
          sortDailyItems(entries.map((entry) => (entry.id === item.id ? saved : entry))),
        );
      } catch (toggleError: unknown) {
        if (!current()) return;
        report(toggleError, 'Could not save that tick.');
        replace(item.id, { done: item.done, doneAt: item.doneAt });
      }
    },
    [claim, replace, report],
  );

  const rename = useCallback(
    async (item: DailyChecklistItem, draft: string) => {
      const title = draft.trim();
      // Blank reverts and unchanged is a no-op, the same rule EditableTitle and
      // the task checklist both follow.
      if (!title || title === item.title) return;

      const current = claim(item.id);
      setActionError(null);
      replace(item.id, { title });

      try {
        await updateDailyChecklistItem(item.id, { title });
      } catch (renameError: unknown) {
        if (!current()) return;
        report(renameError, 'Could not rename that item.');
        replace(item.id, { title: item.title });
      }
    },
    [claim, replace, report],
  );

  const remove = useCallback(
    async (item: DailyChecklistItem) => {
      const current = claim(item.id);
      // Captured before the row goes so a failure puts it back where it was
      // rather than at the bottom -- there is no `position` to restore it by.
      const index = itemsRef.current.findIndex((entry) => entry.id === item.id);

      setActionError(null);
      setItems((entries) => entries.filter((entry) => entry.id !== item.id));

      try {
        await deleteDailyChecklistItem(item.id);
      } catch (deleteError: unknown) {
        if (!current()) return;
        report(deleteError, 'Could not delete that item.');
        setItems((entries) => [...entries.slice(0, index), item, ...entries.slice(index)]);
      }
    },
    [claim, report],
  );

  return {
    items,
    doneCount: items.filter((item) => item.done).length,
    loading,
    loadError,
    actionError,
    dismissActionError: useCallback(() => setActionError(null), []),
    add,
    toggle,
    rename,
    remove,
  };
}

export type DailyChecklist = ReturnType<typeof useDailyChecklist>;
